import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/components/shared/PasswordInput';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Logo, BrandLockup, useSlogan } from '@/components/shared/Logo';
import { PERSONAS } from '@/data/mockData';
import { useApp } from '@/context/AppContext';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { ArrowLeft, ArrowRight, Shield, Sparkles, Check, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import LanguageSwitcher from '@/components/layout/LanguageSwitcher';

const IS_PREVIEW_HOST = typeof window !== 'undefined' &&
  (window.location.hostname.endsWith('.preview.emergentagent.com') || window.location.hostname === 'localhost');
const DEMO_ENABLED = process.env.REACT_APP_ENABLE_DEMO === 'true' && IS_PREVIEW_HOST;

const routeFor = (role) => {
  if (['hm_admin', 'hm_advisor', 'hm_reviewer'].includes(role)) return '/admin';
  if (['client_admin', 'client_hr', 'client_viewer'].includes(role)) return '/client';
  if (role === 'evaluated') return '/me';
  return '/';
};

export default function Login() {
  const { t } = useTranslation();
  const { login, loginAs, formatApiError } = useApp();
  const slogan = useSlogan();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [pendingPersona, setPendingPersona] = useState(null);

  const handleSelect = async (id) => {
    setPendingPersona(id);
    try {
      const u = await loginAs(id);
      toast.success(`Welcome, ${u.name}`, { description: u.position || u.role });
      navigate(u.must_change_password ? '/change-password' : routeFor(u.role));
    } catch (e) {
      toast.error(t('toasts.loginFailed'), { description: formatApiError(e) });
    } finally {
      setPendingPersona(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error(t('toasts.emailPasswordRequired'));
      return;
    }
    setSubmitting(true);
    try {
      const u = await login(email, password);
      toast.success(`Welcome, ${u.name}`);
      navigate(u.must_change_password ? '/change-password' : routeFor(u.role));
    } catch (err) {
      toast.error(t('toasts.loginFailed'), { description: formatApiError(err) });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-background">
      {/* Left: brand panel */}
      <div className="relative hidden lg:flex flex-col bg-gradient-hero text-white p-10 xl:p-14 overflow-hidden">
        <div className="absolute inset-0 dot-pattern opacity-[0.08]" />
        <div className="absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full bg-accent/25 blur-[100px]" />
        <div className="absolute -bottom-40 -left-40 w-[500px] h-[500px] rounded-full bg-primary-glow/20 blur-[100px]" />

        <div className="relative z-10 flex flex-col h-full">
          {/* CTH approved full lockup — primary brand identity of the page */}
          <Link to="/" className="w-fit" data-testid="login-brand-lockup">
            <div className="bg-white rounded-2xl shadow-xl px-8 py-6 inline-block">
              <BrandLockup className="w-56 xl:w-64 h-auto" />
            </div>
          </Link>

          <div className="flex-1 flex flex-col justify-center max-w-md pt-8">
            <Badge className="bg-white/10 text-white border-white/20 backdrop-blur-sm w-fit mb-4">
              <Sparkles className="h-3 w-3 mr-1.5" /> {t('pages.login.ecosystem')}
            </Badge>
            <h2 className="font-display text-3xl xl:text-4xl font-bold tracking-tight leading-[1.12] text-balance">
              {t('pages.login.heroTitle')}
            </h2>
            <p className="mt-2 text-base font-semibold tracking-wide text-[#7FDCDC]" data-testid="login-slogan">{slogan}</p>
            <p className="text-white/75 mt-4 text-lg leading-relaxed text-pretty">
              {t('pages.login.heroBody')}
            </p>

            <div className="mt-8 space-y-3">
              {[t('pages.login.bullets.roleBased'), t('pages.login.bullets.approval'), t('pages.login.bullets.audit'), t('pages.login.bullets.mobile')].map((line) => (
                <div key={line} className="flex items-center gap-3 text-sm text-white/85">
                  <div className="h-5 w-5 rounded-full bg-white/15 flex items-center justify-center shrink-0">
                    <Check className="h-3 w-3" />
                  </div>
                  {line}
                </div>
              ))}
            </div>
          </div>

          <div className="relative z-10 text-xs text-white/50">
            © 2025 Corporate Talent Hub · All rights reserved
          </div>
        </div>
      </div>

      {/* Right: login */}
      <div className="flex flex-col">
        <div className="px-6 md:px-10 py-5 flex items-center justify-between border-b border-border">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-smooth">
            <ArrowLeft className="h-4 w-4" /> {t('common.back')}
          </Link>
          <div className="lg:hidden"><Logo size="sm" /></div>
          <div className="flex items-center gap-3">
            <LanguageSwitcher />
          </div>
        </div>

        <div className="flex-1 flex items-start lg:items-center justify-center p-6 md:p-8 overflow-y-auto">
          <div className="w-full max-w-lg">
            {/* Mobile brand lockup */}
            <div className="lg:hidden mb-6 flex justify-center" data-testid="login-mobile-lockup">
              <BrandLockup className="w-52 h-auto" />
            </div>
            <div className="mb-6">
              <h1 className="font-display text-3xl font-bold tracking-tight">{t('auth.loginTitle')}</h1>
              <p className="text-muted-foreground mt-1.5 text-base">{t('auth.loginSubtitle')}</p>
            </div>

            <form className="space-y-4" onSubmit={handleSubmit}>
              <div className="space-y-2">
                <Label htmlFor="email" className="text-base font-semibold">{t('auth.email')}</Label>
                <Input id="email" data-testid="login-email-input" type="email" placeholder={t('pages.login.emailPh')} className="h-12 text-base" value={email} onChange={(e)=>setEmail(e.target.value)} autoComplete="email" />
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="pw" className="text-base font-semibold">{t('auth.password')}</Label>
                  <Link to="/forgot-password" className="text-sm font-medium text-primary hover:underline" data-testid="login-forgot-link">
                    {t('auth.forgotPassword', { defaultValue: 'Forgot password?' })}
                  </Link>
                </div>
                <PasswordInput id="pw" data-testid="login-password-input" placeholder="••••••••" className="h-12 text-base" value={password} onChange={(e)=>setPassword(e.target.value)} autoComplete="current-password" />
              </div>
              <Button type="submit" data-testid="login-submit-button" disabled={submitting} className="w-full h-12 text-base font-semibold bg-gradient-primary hover:opacity-90">
                {submitting ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" /> {t('auth.signingIn')}</>) : (<>{t('auth.signIn')} <ArrowRight className="ml-2 h-4 w-4" /></>)}
              </Button>
            </form>

            {DEMO_ENABLED && (<>
            <div className="relative my-5">
              <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-border" /></div>
              <div className="relative flex justify-center">
                <span className="px-3 bg-background text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t('pages.login.demoPersonas')}</span>
              </div>
            </div>

            <div className="space-y-2">
              {PERSONAS.map((p) => (
                <button
                  key={p.id}
                  data-testid={`persona-${p.id}-button`}
                  disabled={pendingPersona !== null}
                  onClick={() => handleSelect(p.id)}
                  className="w-full group text-left rounded-xl border border-border bg-card hover:bg-muted/40 hover:border-primary/30 hover:shadow-medium transition-smooth p-3.5 flex items-center gap-3 disabled:opacity-60"
                >
                  <Avatar className="h-10 w-10 border border-border">
                    <AvatarFallback className={`text-xs font-bold text-white ${p.role.startsWith('hm') ? 'bg-gradient-primary' : p.role === 'evaluated' ? 'bg-info' : 'bg-gradient-accent'}`}>{p.avatar}</AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-base font-semibold truncate">{p.name}</p>
                      <Badge variant="outline" className="text-xs font-medium uppercase px-1.5 py-0 h-4 tracking-wider border-border/60 text-muted-foreground">{p.role.replace('_',' ')}</Badge>
                    </div>
                    <p className="text-sm text-muted-foreground truncate mt-0.5">{p.description}</p>
                  </div>
                  {pendingPersona === p.id ? (
                    <Loader2 className="h-4 w-4 animate-spin text-primary" />
                  ) : (
                    <ArrowRight className="h-4 w-4 text-muted-foreground group-hover:text-primary group-hover:translate-x-0.5 transition-smooth" />
                  )}
                </button>
              ))}
            </div>
            </>)}

            <div className="mt-6 flex items-center gap-2 text-sm text-muted-foreground rounded-lg bg-muted/50 p-3 border border-border/60">
              <Shield className="h-4 w-4 text-primary shrink-0" />
              <span>{t('pages.login.footnote')}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
